const WINDOW_MS = 3 * 60 * 1000;
const LIVE_TRANSACTION_LIMIT = 4;
const REAL_TRANSACTION_MAX_AGE_MS = 5 * 60 * 1000;
const PHONE_INPUT_MAX = 12;
const TEXT_USERNAME_MIN = 4;
const TEXT_USERNAME_MAX = 9;
const DIGITS = "0123456789";
const ALNUM = "abcdefghijklmnopqrstuvwxyz0123456789";
const LETTER_USERNAME_ENDINGS = [
  "an", "en", "on", "in", "un", "er", "ar", "or", "ir", "ur",
  "le", "el", "al", "il", "ol", "ul", "ey", "ay", "oy", "ie",
  "ei", "ai", "ao", "ea", "ou", "io", "ia", "eo", "ae", "ee",
  "oo", "ui", "ua", "ue", "au", "yi", "ya", "ye", "yo", "yu",
  "ng", "ck", "th", "sh", "ch", "ll", "nn", "ss", "tt", "ff",
  "us", "is", "as", "es", "os", "am", "em", "im", "om", "um",
  "ka", "ke", "ki", "ko", "ku", "ma", "me", "mi", "mo", "mu",
  "na", "ne", "ni", "no", "nu", "la", "li", "lo", "lu", "ra",
  "re", "ri", "ro", "ru", "ta", "te", "ti", "to", "tu", "sa",
  "se", "si", "so", "su", "da", "de", "di", "do", "ba", "be",
  "bi", "bo", "pa", "pe", "pi", "po", "ha", "he", "hi", "ho",
  "ga", "ge", "gi", "go", "fa", "fe", "fi", "fo", "ja", "je",
  "ji", "jo", "wa", "we", "wi", "wo", "va", "ve", "vi", "vo",
];
const NUMBER_USERNAME_ENDINGS = Array.from({ length: 100 }, (_, value) => String(value).padStart(2, "0"));
export const TEXT_USERNAME_ENDINGS = [...LETTER_USERNAME_ENDINGS, ...NUMBER_USERNAME_ENDINGS];
const DEPOSIT_TYPE = 1;
const WITHDRAWAL_TYPE = 2;

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const field = (row, name) => {
  if (!row) {
    return undefined;
  }
  if (row[name] !== undefined && row[name] !== null) {
    return row[name];
  }
  const pascalName = name.charAt(0).toUpperCase() + name.slice(1);
  return row[pascalName];
};

const hashSeed = (text) => {
  let hash = 2166136261;
  const value = String(text);
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const mulberry32 = (seed) => () => {
  let next = (seed + 0x6d2b79f5) >>> 0;
  seed = next;
  let t = next;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (t ^ (t >>> 14)) >>> 0;
};

const randInt = (rand, min, max) => {
  const lower = Math.min(min, max);
  const upper = Math.max(min, max);
  return lower + (rand() % (upper - lower + 1));
};

const randomString = (rand, chars, length) => {
  let value = "";
  for (let i = 0; i < length; i += 1) {
    value += chars[rand() % chars.length];
  }
  return value;
};

const maskUsername = (username) =>
  username.length <= 2 ? username : "*".repeat(username.length - 2) + username.slice(-2);

const normalizeDialingCodes = (dialingCodes) => {
  if (!Array.isArray(dialingCodes)) {
    return [];
  }

  return dialingCodes
    .map((code) => ({
      value: String(field(code, "value") ?? ""),
      min: toNumber(field(code, "min"), 8),
      max: toNumber(field(code, "max"), 15),
    }))
    .filter((code) => code.value !== "")
    .sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
};

const normalizeVipIcons = (vipIcons) => {
  if (!Array.isArray(vipIcons)) {
    return [];
  }

  return vipIcons
    .map((icon) => ({
      id: toNumber(field(icon, "id")),
      icon: String(field(icon, "icon") ?? ""),
      rank: toNumber(field(icon, "rank")),
      obtainMinDeposit: toNumber(field(icon, "obtainMinDeposit")),
    }))
    .filter((icon) => icon.icon !== "")
    .sort((a, b) => a.id - b.id);
};

const generatePhoneUsername = (rand, dialingCodes) => {
  const code = dialingCodes[rand() % dialingCodes.length];
  const localMax = Math.min(code.max, PHONE_INPUT_MAX);
  const localMin = Math.min(Math.max(code.min, 1), Math.max(localMax, 1));
  return code.value + randomString(rand, DIGITS, randInt(rand, localMin, Math.max(localMax, localMin)));
};

const generateTextUsername = (rand, usedEndings) => {
  const length = randInt(rand, TEXT_USERNAME_MIN, TEXT_USERNAME_MAX);
  const available = TEXT_USERNAME_ENDINGS.filter((ending) => !usedEndings.has(ending));
  const pool = available.length ? available : TEXT_USERNAME_ENDINGS;
  const ending = pool[rand() % pool.length];
  usedEndings.add(ending);
  return randomString(rand, ALNUM, length - 2) + ending;
};

const generateUsername = (rand, isEZUsernameEnabled, dialingCodes, usedEndings) =>
  maskUsername(
    isEZUsernameEnabled && dialingCodes.length
      ? generatePhoneUsername(rand, dialingCodes)
      : generateTextUsername(rand, usedEndings)
  );

const wholeAmount = (rand, minAmount, maxAmount) => {
  const minNumber = toNumber(minAmount);
  const maxNumber = toNumber(maxAmount);
  let lower = Math.ceil(minNumber);
  let upper = Math.floor(maxNumber);

  if (upper < lower) {
    const rounded = Math.round(minNumber);
    lower = rounded;
    upper = rounded;
  }

  return randInt(rand, lower, upper);
};

const roundDepositAmount = (amount) => {
  const rounded = Math.floor(amount / 10) * 10;
  return rounded > 0 ? rounded : amount;
};

export const formatLiveTransactionAmount = (amount) => {
  const number = Number(amount);
  if (!Number.isFinite(number)) {
    return "";
  }

  return number.toFixed(2);
};

const transactionTime = (row) => {
  const value = Date.parse(field(row, "transactionDateTime"));
  return Number.isFinite(value) ? value : 0;
};

export const readFakeTransactionCache = (payload) => {
  if (Array.isArray(payload)) {
    return { settings: payload, vipIcons: [] };
  }

  if (!payload || typeof payload !== "object") {
    return { settings: [], vipIcons: [] };
  }

  return {
    settings: Array.isArray(payload.settings) ? payload.settings : [],
    vipIcons: Array.isArray(payload.vipIcons) ? payload.vipIcons : [],
  };
};

export const getFakeTransactionWindowStart = (serverUtcMs) =>
  Math.floor(toNumber(serverUtcMs) / WINDOW_MS) * WINDOW_MS;

export const getFakeTransactionRefreshMs = (serverUtcMs) => {
  const windowStart = getFakeTransactionWindowStart(serverUtcMs);
  return Math.max(1000, windowStart + WINDOW_MS - toNumber(serverUtcMs));
};

const enabledSettings = (settings) => {
  if (!Array.isArray(settings)) {
    return [];
  }

  return settings
    .filter((setting) => field(setting, "isEnabled") === true)
    .filter((setting) => {
      const type = Number(field(setting, "transactionTypeId"));
      return type === DEPOSIT_TYPE || type === WITHDRAWAL_TYPE;
    })
    .sort((a, b) => Number(field(a, "transactionTypeId")) - Number(field(b, "transactionTypeId")));
};

const iconsForTransaction = (vipIcons, transactionTypeId, amount) => {
  if (transactionTypeId !== DEPOSIT_TYPE) {
    return vipIcons;
  }

  const unlocked = vipIcons.filter((icon) => icon.obtainMinDeposit > 0 && icon.obtainMinDeposit <= amount);
  if (unlocked.length === 0) {
    return vipIcons;
  }

  const minRank = unlocked.reduce((highest, icon) => Math.max(highest, icon.rank), 0);
  const eligible = vipIcons.filter((icon) => icon.rank >= minRank);
  return eligible.length ? eligible : vipIcons;
};

const generateRowsForSetting = (setting, options) => {
  const transactionTypeId = Number(field(setting, "transactionTypeId"));
  const windowStart = getFakeTransactionWindowStart(options.serverUtcMs);
  const settingId = field(setting, "id") ?? transactionTypeId;
  const rand = mulberry32(hashSeed(`${settingId}:${windowStart}`));
  const dialingCodes = options.dialingCodes;
  const vipIcons = options.vipIcons;
  const count = options.count;

  return Array.from({ length: count }, (_, index) => {
    const username = generateUsername(rand, options.isEZUsernameEnabled, dialingCodes, options.usedEndings);
    const pickedAmount = wholeAmount(rand, field(setting, "minAmount"), field(setting, "maxAmount"));
    const amount = transactionTypeId === DEPOSIT_TYPE ? roundDepositAmount(pickedAmount) : pickedAmount;
    const eligibleIcons = iconsForTransaction(vipIcons, transactionTypeId, amount);
    const icon = eligibleIcons.length ? eligibleIcons[rand() % eligibleIcons.length].icon : "";

    return {
      username,
      amount,
      transactionTypeId,
      icon,
      transactionDateTime: new Date(windowStart + (count - index) * 1000).toISOString(),
      isFake: true,
    };
  });
};

export const generateFakeTransactions = ({
  settings,
  vipIcons,
  isEZUsernameEnabled = false,
  dialingCodes,
  serverUtcMs,
  count = LIVE_TRANSACTION_LIMIT,
} = {}) => {
  const normalizedCodes = normalizeDialingCodes(dialingCodes);
  const normalizedIcons = normalizeVipIcons(vipIcons);
  const usedEndings = new Set();
  const rows = enabledSettings(settings).flatMap((setting) =>
    generateRowsForSetting(setting, {
      isEZUsernameEnabled: isEZUsernameEnabled === true,
      dialingCodes: normalizedCodes,
      vipIcons: normalizedIcons,
      serverUtcMs,
      count,
      usedEndings,
    })
  );

  return {
    rows,
    nextRefreshMs: getFakeTransactionRefreshMs(serverUtcMs),
    windowStart: getFakeTransactionWindowStart(serverUtcMs),
  };
};

const isFreshRealTransaction = (row, serverUtcMs) => {
  const time = transactionTime(row);
  return time > 0 && toNumber(serverUtcMs) - time < REAL_TRANSACTION_MAX_AGE_MS;
};

export const getLiveTransactionRefreshMs = (realRows, serverUtcMs, nextWindowMs) => {
  const now = toNumber(serverUtcMs);
  const expiries = (Array.isArray(realRows) ? realRows : [])
    .map((row) => transactionTime(row) + REAL_TRANSACTION_MAX_AGE_MS - now)
    .filter((ms) => ms > 0);
  const soonestExpiry = expiries.length ? Math.min(...expiries) : toNumber(nextWindowMs);

  return Math.max(1000, Math.min(toNumber(nextWindowMs), soonestExpiry));
};

const sameTransaction = (left, right) =>
  left?.username === right?.username &&
  left?.amount === right?.amount &&
  Number(field(left, "transactionTypeId")) === Number(field(right, "transactionTypeId")) &&
  field(left, "transactionDateTime") === field(right, "transactionDateTime");

export const mergeLiveTransactions = (realRows, fakeRows, serverUtcMs, limit = LIVE_TRANSACTION_LIMIT) => {
  const source = Array.isArray(realRows) ? realRows : [];
  const fakes = Array.isArray(fakeRows) ? fakeRows : [];
  const real = (fakes.length > 0 ? source.filter((row) => isFreshRealTransaction(row, serverUtcMs)) : source).slice(0, limit);
  const room = Math.max(0, limit - real.length);
  const fake = fakes.slice(0, room);

  return [...real, ...fake].sort((a, b) => transactionTime(b) - transactionTime(a));
};

export const refreshFakeTransactions = (currentRows, fakeRows, serverUtcMs, limit = LIVE_TRANSACTION_LIMIT) => {
  const current = Array.isArray(currentRows) ? currentRows.slice(0, limit) : [];
  const fakes = Array.isArray(fakeRows) ? fakeRows : [];
  if (current.length === 0) {
    return fakes.slice(0, limit);
  }

  const used = new Set();
  const next = current.map((row) => {
    if (!row?.isFake && isFreshRealTransaction(row, serverUtcMs)) {
      return row;
    }

    if (row?.isFake) {
      const matchIndex = fakes.findIndex((fake, index) => !used.has(index) && sameTransaction(fake, row));
      if (matchIndex >= 0) {
        used.add(matchIndex);
        return fakes[matchIndex];
      }
    }

    return null;
  });

  const unused = fakes.filter((_, index) => !used.has(index));
  let extraIndex = 0;
  for (let index = 0; index < next.length; index += 1) {
    if (next[index] === null && extraIndex < unused.length) {
      next[index] = unused[extraIndex];
      extraIndex += 1;
    }
  }

  const filled = next.filter(Boolean);
  while (filled.length < limit && extraIndex < unused.length) {
    filled.push(unused[extraIndex]);
    extraIndex += 1;
  }

  return filled.slice(0, limit);
};

export const FAKE_TRANSACTION_WINDOW_MS = WINDOW_MS;
export const FAKE_TRANSACTION_LIMIT = LIVE_TRANSACTION_LIMIT;
